import React, { FC, ReactNode, useMemo, useRef } from 'react';
import useElementResize from 'hooks/useElementResize';
import ZestLogo from '../ZestLogo';
import { SAuthFlowLayout } from './AuthFlowLayout.style';

type TAuthFlowLayoutProps = {
  /** Hero heading above the card (e.g. "Built for MSP Success."). Omit for none (login). */
  title?: ReactNode;
  /** Optional hero subtitle (blue), e.g. the free-trial note. */
  subtitle?: ReactNode;
  /** The screen's card. */
  children: ReactNode;
};

// Never enlarge past the Figma design; only shrink to fit shorter viewports.
const MAX_SCALE = 1;
// Breathing room (px) kept around the content inside the hero.
const HERO_GUTTER = 24;

/**
 * Shared shell for the ZEST-738 auth flow: light grid hero, centered Zest logo,
 * heading, the screen's card, and the footer.
 *
 * The logo + heading + card group is rendered at full Figma size, then scaled
 * uniformly (transform:scale, computed from the viewport) so it always fits
 * without scrolling while keeping the design's exact proportions. Recomputes on
 * resize and whenever the card's own height changes (e.g. password checklist).
 *
 * TODO(ZEST-738): swap the text/PNG logo for the exported Zest SVG (315:4732)
 * and the CSS grid for the exported hero background once assets are available.
 */
const AuthFlowLayout: FC<TAuthFlowLayoutProps> = ({
  title,
  subtitle,
  children,
}) => {
  const heroRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  // Observe sizes via the repo's useElementResize (globally mocked in tests)
  // rather than a raw `new ResizeObserver`, so these screens can be rendered
  // under RTL/jsdom. offset-based sizes are layout sizes, unaffected by the
  // scale transform, so this can't feed back on itself.
  const hero = useElementResize({ ref: heroRef });
  const content = useElementResize({ ref: contentRef });

  // Scale the group down to fit the hero without scrolling, preserving the
  // design's proportions.
  const scale = useMemo(() => {
    const availW = hero.width - HERO_GUTTER * 2;
    const availH = hero.height - HERO_GUTTER * 2;
    if (content.width <= 0 || content.height <= 0 || availW <= 0 || availH <= 0)
      return 1;
    const next = Math.min(
      availW / content.width,
      availH / content.height,
      MAX_SCALE,
    );
    return next > 0 ? next : 1;
  }, [hero.width, hero.height, content.width, content.height]);

  return (
    <SAuthFlowLayout>
      <div className="auth-flow__hero" ref={heroRef}>
        <div
          className="auth-flow__scale"
          style={{ transform: `scale(${scale})` }}
        >
          <div className="auth-flow__content" ref={contentRef}>
            <ZestLogo className="auth-flow__logo" />
            {(title || subtitle) && (
              <div className="auth-flow__heading">
                {title && <h1 className="auth-flow__title">{title}</h1>}
                {subtitle && <p className="auth-flow__subtitle">{subtitle}</p>}
              </div>
            )}
            {children}
          </div>
        </div>
      </div>
      <footer className="auth-flow__footer">
        <span className="auth-flow__copyright">
          ©2026 ZEST · All rights reserved.
        </span>
        <nav className="auth-flow__footer-links">
          <a
            href="https://www.zestmsp.com/terms-and-conditions"
            target="_blank"
            rel="noreferrer"
          >
            Term of use
          </a>
          <a
            href="https://www.zestmsp.com/privacy-policy"
            target="_blank"
            rel="noreferrer"
          >
            Privacy policy
          </a>
          <a
            href="https://www.zestmsp.com/security"
            target="_blank"
            rel="noreferrer"
          >
            Security
          </a>
        </nav>
      </footer>
    </SAuthFlowLayout>
  );
};

export default AuthFlowLayout;
